import { Tooltip } from "antd";
import { ReactElement, useState } from "react";
import { PieSectorDataItem, PieSectorShapeProps, Sector } from "recharts";

type ChartActiveShapeRenderProps = {
  onMouseEnter: (data: PieSectorDataItem, index: number) => void;
  renderShape: (props: PieSectorShapeProps, index?: number | string) => ReactElement<SVGElement>;
};

export const useChartActiveShapeRender = (): ChartActiveShapeRenderProps => {
  const [activeShapeIndex, setActiveShapeIndex] = useState<number>(1);

  const renderShape = (props: PieSectorShapeProps, index?: number | string): ReactElement<SVGElement> => {
    if (index !== activeShapeIndex) {
      return (
        <Sector
          cx={props.cx}
          cy={props.cy}
          innerRadius={props.innerRadius}
          outerRadius={props.outerRadius}
          startAngle={props.startAngle}
          endAngle={props.endAngle}
          fill={props.fill}
        />
      );
    }

    const RADIAN = Math.PI / 180;
    const { cx, cy, midAngle = 0, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent = 0 } = props;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const sx = cx + (outerRadius + 10) * cos;
    const sy = cy + (outerRadius + 10) * sin;
    const mx = cx + (outerRadius + 15) * cos;
    const my = cy + (outerRadius + 15) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 20;
    const ey = my;
    const textAnchor = cos >= 0 ? "start" : "end";

    return (
      <g>
        <text x={cx} y={cy - 15} dy={8} textAnchor="middle" style={{ marginBottom: 6 }}>
          {payload.label}
        </text>
        <text x={cx} y={cy + 15} dy={8} textAnchor="middle" style={{ fontSize: 24, fontWeight: 600 }} fill={fill}>
          {`${(percent * 100).toFixed(2)}%`}
        </text>

        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />

        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius + 6}
          outerRadius={outerRadius + 10}
          fill={fill}
        />

        <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" />

        <Tooltip title={payload.formattedValue}>
          <text
            x={ex + (cos >= 0 ? 1 : -1) * 12 + (cos >= 0 ? -5 : 5)}
            y={ey + 5}
            textAnchor={textAnchor}
            style={{ fontSize: 15, fontWeight: 600 }}
            fill={fill}
          >
            {payload.formattedValue}
          </text>
        </Tooltip>
      </g>
    );
  };

  const onMouseEnter = (_: PieSectorDataItem, index: number) => setActiveShapeIndex(index);

  return { onMouseEnter, renderShape };
};
